import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';
import './MyRegistrations.css';

const MyCertificates = ({ user, onLogout, onSwitchView }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [toast, setToast] = useState(null);

  const fetchCertificates = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`/api/learner/certificates/${encodeURIComponent(user.email)}`);
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setCertificates(data.data || []);
      } else {
        setCertificates([]);
      }
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
      setToast({ type: 'error', msg: 'Unable to load certificates. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, [user]);

  const uniqueTemplateTypes = useMemo(() => {
    const set = new Set();
    certificates.forEach(c => {
      if (c.TemplateType) set.add(c.TemplateType);
    });
    return Array.from(set);
  }, [certificates]);

  const getTemplateTypeLabel = (type) => {
    if (type === 'MCMC') return 'CCoE Standard';
    if (type === 'RCC') return 'RCC Joint Certs';
    return type;
  };

  const filteredCerts = certificates.filter(cert => {
    const matchesSearch =
      (cert.CourseName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cert.CertificateNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cert.CertificationBody || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;
    if (filterType !== 'ALL') return cert.TemplateType === filterType;
    return true;
  });

  const formatDMY = (dateStr) => {
    if (!dateStr) return 'TBD';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div className="profile-page-layout">
      {/* Floating Toast Notification */}
      {toast && (
        <div className={`profile-toast ${toast.type}`}>
          <div className="profile-toast-icon">
            <SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={20} />
          </div>
          <div className="profile-toast-content">
            <span className="profile-toast-title">{toast.type === 'success' ? 'Success!' : 'Notice'}</span>
            <span className="profile-toast-desc">{toast.msg}</span>
          </div>
          <button className="profile-toast-close" onClick={() => setToast(null)}>
            <SvgIcon name="x" size={16} />
          </button>
        </div>
      )}

      {/* Sidebar Navigation */}
      <Sidebar activeView="myCertificates" onSwitchView={onSwitchView} onLogout={onLogout} user={user} />

      {/* Main Content Area */}
      <main className="profile-main-content">
        <header className="profile-top-bar my-reg-top-bar">
          <div className="profile-top-bar-left">
            <SvgIcon name="award" size={24} color="#059669" />
            <h1 className="admin-dashboard-title my-reg-header-title">My Certificates of Completion</h1>
          </div>
          <div className="profile-top-bar-right my-reg-header-actions">
            <button
              className="my-reg-btn"
              style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155' }}
              onClick={fetchCertificates}
              title="Refresh Certificate List"
            >
              <SvgIcon name="refresh" size={15} />
              <span>Refresh</span>
            </button>
          </div>
        </header>

        <div className="my-reg-content-container" style={{ padding: '1.5rem 2rem' }}>
          {/* Top Summary Banner */}
          <div
            style={{
              background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)',
              borderRadius: '12px',
              padding: '1.75rem 2rem',
              color: '#ffffff',
              marginBottom: '2rem',
              boxShadow: '0 10px 25px -5px rgba(6, 78, 59, 0.25)',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1.5rem'
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <SvgIcon name="graduationCap" size={20} />
                <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: '#a7f3d0' }}>
                  Verified Credentials Showcase
                </span>
              </div>
              <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: 800, color: '#ffffff' }}>
                Congratulations, {user?.fullName || user?.username || 'Learner'}!
              </h2>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#d1fae5', maxWidth: '600px', lineHeight: 1.5 }}>
                Here are your official certificates of completion earned through 100% course session attendance at the Cybersecurity Center of Excellence.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(8px)', padding: '0.9rem 1.4rem', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 900, lineHeight: 1, color: '#ffffff' }}>
                  {certificates.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a7f3d0', marginTop: '4px', fontWeight: 600 }}>
                  Earned Certificates
                </div>
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(8px)', padding: '0.9rem 1.4rem', borderRadius: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 900, lineHeight: 1, color: '#ffffff' }}>
                  {certificates.reduce((acc, c) => acc + (c.HoursCompleted || 0), 0)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a7f3d0', marginTop: '4px', fontWeight: 600 }}>
                  Training Hours
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filter Controls */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setFilterType('ALL')}
                style={{
                  padding: '0.45rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.825rem',
                  fontWeight: 700,
                  border: '1px solid',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: filterType === 'ALL' ? '#065f46' : '#ffffff',
                  color: filterType === 'ALL' ? '#ffffff' : '#475569',
                  borderColor: filterType === 'ALL' ? '#065f46' : '#cbd5e1'
                }}
              >
                All Certificates ({certificates.length})
              </button>
              {uniqueTemplateTypes.map((tType) => (
                <button
                  key={tType}
                  type="button"
                  onClick={() => setFilterType(tType)}
                  style={{
                    padding: '0.45rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    border: '1px solid',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: filterType === tType ? '#065f46' : '#ffffff',
                    color: filterType === tType ? '#ffffff' : '#475569',
                    borderColor: filterType === tType ? '#065f46' : '#cbd5e1'
                  }}
                >
                  {getTemplateTypeLabel(tType)} ({certificates.filter(c => c.TemplateType === tType).length})
                </button>
              ))}
            </div>

            <div style={{ position: 'relative', width: '280px', maxWidth: '100%' }}>
              <input
                type="text"
                placeholder="Search certificate or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem 0.5rem 2.25rem',
                  fontSize: '0.85rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
              <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                <SvgIcon name="search" size={16} />
              </span>
            </div>
          </div>

          {/* Certificates Grid Display */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#64748b' }}>
              <div style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}>⏳</div>
              <p style={{ margin: 0, fontWeight: 600 }}>Loading your earned certificates...</p>
            </div>
          ) : filteredCerts.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px dashed #cbd5e1'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem', color: '#94a3b8' }}>
                <SvgIcon name="graduationCap" size={44} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1.2rem', fontWeight: 700 }}>
                {searchTerm || filterType !== 'ALL' ? 'No certificates match your search.' : 'No Certificates Issued Yet'}
              </h3>
              <p style={{ margin: '0 0 1.25rem 0', color: '#64748b', fontSize: '0.9rem', maxWidth: '480px', marginInline: 'auto' }}>
                {searchTerm || filterType !== 'ALL'
                  ? 'Try clearing your search query or switching filters.'
                  : 'Complete all training sessions with 100% attendance rate. Once your attendance is verified by the administrator, your official certificate will appear here.'}
              </p>
              <button
                type="button"
                onClick={() => onSwitchView('calendar')}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: '#047857',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Browse Course Calendar
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '1.5rem'
              }}
            >
              {filteredCerts.map((cert) => {
                const isRcc = cert.TemplateType === 'RCC';
                return (
                  <div
                    key={cert.CertificateID}
                    style={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      border: isRcc ? '1.5px solid #0284c7' : '1.5px solid #059669',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                  >
                    {/* Card Header */}
                    <div
                      style={{
                        background: isRcc
                          ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                          : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        padding: '1rem 1.25rem',
                        color: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <SvgIcon name="award" size={18} />
                        <span style={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          {isRcc ? 'CCoE & RCC Joint Certificate' : 'CCoE Official Certificate'}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          background: 'rgba(255,255,255,0.25)',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: 700
                        }}
                      >
                        {isRcc ? 'RCC' : 'MCMC'}
                      </span>
                    </div>

                    {/* Card Body */}
                    <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <h3
                        style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '1.05rem',
                          fontWeight: 800,
                          color: '#0f172a',
                          lineHeight: 1.4
                        }}
                      >
                        {cert.CourseName}
                      </h3>

                      <div style={{ fontSize: '0.825rem', color: '#475569', marginBottom: '1rem', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ color: '#64748b' }}>Recipient:</span>
                          <strong style={{ color: '#0f172a' }}>{cert.LearnerFullName}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ color: '#64748b' }}>Awarded Date:</span>
                          <strong>{formatDMY(cert.AwardedDate)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ color: '#64748b' }}>Training Hours:</span>
                          <strong>{cert.HoursCompleted} Hours Completed</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                          <span style={{ color: '#64748b' }}>Verification No:</span>
                          <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', color: '#0369a1' }}>
                            {cert.CertificateNumber}
                          </code>
                        </div>
                      </div>

                      {/* Action Download / View Buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                        <a
                          href={`/api/certificates/download/${cert.CertificateNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '0.55rem 0.75rem',
                            borderRadius: '8px',
                            background: isRcc ? '#0284c7' : '#059669',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '0.825rem',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          <SvgIcon name="download" size={14} />
                          <span>Download PDF</span>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyCertificates;
